import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { Plus, Minus, ShoppingBag, X } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { buildImageUrl } from "../lib/api";
import { flyToCart } from "../lib/animations";
import { reachGoal } from "../lib/metrika";
import { toast } from "sonner";

const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const [weightKg, setWeightKg] = useState(1); // for variable (1 or 2 kg)
  const [qty, setQty] = useState(1); // for fixed (pcs)
  const cardRef = useRef(null);
  const imgRef = useRef(null);
  const closeTimerRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -60px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const [photoExpanded, setPhotoExpanded] = useState(false);
  const [photoClosing, setPhotoClosing] = useState(false);

  const openPhoto = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setPhotoClosing(false);
    setPhotoExpanded(true);
  };

  const closePhoto = () => {
    if (!photoExpanded || photoClosing) return;
    setPhotoClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      setPhotoExpanded(false);
      setPhotoClosing(false);
      closeTimerRef.current = null;
    }, 380);
  };

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (photoExpanded) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [photoExpanded]);

  const isVariable = product.weight_type === "variable";

  const displayPrice = isVariable
    ? Math.round(product.price * weightKg)
    : product.price * qty;

  const handleAdd = () => {
    flyToCart(imgRef.current);
    if (isVariable) {
      addItem(product, weightKg, "кг");
      reachGoal("add_to_cart", {
        product_id: product.id,
        product_name: product.name,
        quantity: weightKg,
        unit: "кг",
        price: displayPrice,
        currency: "RUB",
      });
      toast.success(`${product.name} · ${weightKg} кг добавлено`);
    } else {
      addItem(product, qty, "шт");
      reachGoal("add_to_cart", {
        product_id: product.id,
        product_name: product.name,
        quantity: qty,
        unit: "шт",
        price: displayPrice,
        currency: "RUB",
      });
      toast.success(`${product.name} · ${qty} шт добавлено`);
    }
  };

  return (
    <article
      ref={cardRef}
      data-testid={`product-card-${product.id}`}
      className={`card-hover product-card-reveal scroll-reveal ${visible ? "in-view" : ""} group bg-white border border-[#E8E3DA] rounded-[24px] overflow-hidden flex flex-col`}
      style={{ "--card-reveal-delay": visible ? `${(index % 6) * 90}ms` : "0ms" }}
    >
      <div
        className="relative overflow-hidden aspect-[4/3] bg-[#F2EBE1] sm:cursor-default cursor-pointer"
        onClick={() => { if (window.innerWidth < 640) openPhoto(); }}
      >
        <img
          ref={imgRef}
          src={buildImageUrl(product.image_url)}
          alt={product.name}
          loading="lazy"
          className="card-image absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-4 left-4 chip bg-white/95 backdrop-blur-sm">
          {product.category}
        </div>
        {product.weight_type === "fixed" && product.weight && (
          <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium bg-[#2C2520]/90 text-[#F9F6F0]">
            {product.weight}
          </div>
        )}
      </div>

      <div className="p-3 sm:p-6 flex flex-col flex-1">
        <h3 className="font-display text-base sm:text-2xl leading-tight text-[#2C2520] mb-1 sm:mb-2">
          {product.name}
        </h3>
        <p className="text-xs sm:text-sm text-[#5C4F45] leading-relaxed mb-3 sm:mb-5 flex-1 hidden sm:block">
          {product.description}
        </p>

        {isVariable ? (
          <div className="flex items-center gap-1 sm:gap-2 mb-3 sm:mb-5">
            <button
              data-testid={`weight-1kg-${product.id}`}
              onClick={() => setWeightKg(1)}
              className={`flex-1 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium border transition-all ${
                weightKg === 1
                  ? "bg-[#2C2520] text-[#F9F6F0] border-[#2C2520]"
                  : "bg-white text-[#5C4F45] border-[#E8E3DA] hover:border-[#C65D3B]"
              }`}
            >
              1 кг
            </button>
            <button
              data-testid={`weight-2kg-${product.id}`}
              onClick={() => setWeightKg(2)}
              className={`flex-1 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium border transition-all ${
                weightKg === 2
                  ? "bg-[#2C2520] text-[#F9F6F0] border-[#2C2520]"
                  : "bg-white text-[#5C4F45] border-[#E8E3DA] hover:border-[#C65D3B]"
              }`}
            >
              2 кг
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between mb-3 sm:mb-5">
            <div className="label-eyebrow hidden sm:block">Количество</div>
            <div className="flex items-center gap-1 sm:gap-2 w-full sm:w-auto justify-between sm:justify-start">
              <button
                data-testid={`qty-minus-${product.id}`}
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="w-7 h-7 sm:w-9 sm:h-9 rounded-full border border-[#E8E3DA] flex items-center justify-center hover:border-[#C65D3B] transition-colors"
              >
                <Minus size={12} />
              </button>
              <span
                className="w-6 sm:w-8 text-center font-medium text-[#2C2520] text-sm"
                data-testid={`qty-value-${product.id}`}
              >
                {qty}
              </span>
              <button
                data-testid={`qty-plus-${product.id}`}
                onClick={() => setQty((q) => q + 1)}
                className="w-7 h-7 sm:w-9 sm:h-9 rounded-full border border-[#E8E3DA] flex items-center justify-center hover:border-[#C65D3B] transition-colors"
              >
                <Plus size={12} />
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-3 sm:pt-4 border-t border-[#E8E3DA]">
          <div>
            <div className="label-eyebrow text-[10px] hidden sm:block">Цена</div>
            <div className="font-display text-xl sm:text-3xl text-[#2C2520] leading-none">
              {displayPrice}
              <span className="font-body font-normal text-sm sm:text-lg ml-0.5 sm:ml-1 text-[#5C4F45] align-baseline">₽</span>
            </div>
          </div>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            className="btn-primary text-xs sm:text-sm px-3 sm:px-4 py-2"
          >
            <ShoppingBag size={14} className="hidden sm:block" />
            <ShoppingBag size={12} className="sm:hidden" />
            <span className="hidden sm:inline">В корзину</span>
          </button>
        </div>
      </div>
      {photoExpanded && ReactDOM.createPortal(
        <div
          className={`product-photo-modal-backdrop ${photoClosing ? "is-closing" : ""} fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-4 sm:hidden`}
          onClick={closePhoto}
        >
          <div
            className={`modal-pop product-photo-modal ${photoClosing ? "is-closing" : ""} bg-white rounded-[24px] overflow-hidden w-full max-w-sm flex flex-col relative`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm shadow flex items-center justify-center text-[#2C2520]"
              onClick={closePhoto}
            >
              <X size={16} />
            </button>
            <div className="relative overflow-hidden aspect-[4/3] bg-[#F2EBE1]">
              <img
                src={buildImageUrl(product.image_url)}
                alt={product.name}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 chip bg-white/95 backdrop-blur-sm">
                {product.category}
              </div>
              {product.weight_type === "fixed" && product.weight && (
                <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full text-xs font-medium bg-[#2C2520]/90 text-[#F9F6F0]">
                  {product.weight}
                </div>
              )}
            </div>
            <div className="p-6 flex flex-col">
              <h3 className="font-display text-2xl leading-tight text-[#2C2520] mb-2">{product.name}</h3>
              <p className="text-sm text-[#5C4F45] leading-relaxed mb-5">{product.description}</p>
              {isVariable ? (
                <div className="flex items-center gap-2 mb-5">
                  <button
                    onClick={() => setWeightKg(1)}
                    className={`flex-1 py-2 rounded-full text-sm font-medium border transition-all ${weightKg === 1 ? "bg-[#2C2520] text-[#F9F6F0] border-[#2C2520]" : "bg-white text-[#5C4F45] border-[#E8E3DA]"}`}
                  >1 кг</button>
                  <button
                    onClick={() => setWeightKg(2)}
                    className={`flex-1 py-2 rounded-full text-sm font-medium border transition-all ${weightKg === 2 ? "bg-[#2C2520] text-[#F9F6F0] border-[#2C2520]" : "bg-white text-[#5C4F45] border-[#E8E3DA]"}`}
                  >2 кг</button>
                </div>
              ) : (
                <div className="flex items-center justify-between mb-5">
                  <div className="label-eyebrow">Количество</div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-9 h-9 rounded-full border border-[#E8E3DA] flex items-center justify-center hover:border-[#C65D3B] transition-colors"><Minus size={12} /></button>
                    <span className="w-8 text-center font-medium text-[#2C2520] text-sm">{qty}</span>
                    <button onClick={() => setQty((q) => q + 1)} className="w-9 h-9 rounded-full border border-[#E8E3DA] flex items-center justify-center hover:border-[#C65D3B] transition-colors"><Plus size={12} /></button>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between pt-4 border-t border-[#E8E3DA]">
                <div>
                  <div className="label-eyebrow text-[10px]">Цена</div>
                  <div className="font-display text-3xl text-[#2C2520] leading-none">
                    {displayPrice}<span className="font-body font-normal text-lg ml-1 text-[#5C4F45] align-baseline">₽</span>
                  </div>
                </div>
                <button onClick={handleAdd} className="btn-primary text-sm px-4 py-2">
                  <ShoppingBag size={14} />В корзину
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </article>
  );
};

export default ProductCard;
